import { useEffect, useState } from 'react';
import appConfig from '../Config/appConfig';
import axios from 'axios';
import { createRequestContext, getJourneyId, getLaunchProperties, getRawLaunchProperty, isEpicHandshakeLogEnabled, isErrorDetailsEnabled, trackTelemetry } from '../Utils/telemetry';

const url = appConfig.APIBaseURL;

// Client-side/transport failures (no HTTP response reached) — safe to retry.
const isNetworkError = (error) =>
  !error.response && (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ECONNRESET' ||
    error.message === 'Network Error'
  );

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_RETRIES = 2;

const parseLaunchContextParams = (search) => {
  const params = new URLSearchParams(search || '');
  const keysToExtract = ['vpaccPatient', 'selfEfficacy', 'dateOfBirth', 'height', 'weightKg', 'cuffSize', 'programExtension', 'INCONTROL', 'inControl'];
  const extractedParams = {};

  keysToExtract.forEach((key) => {
    const value = params.get(key);
    if (value !== null && value !== undefined && value !== '') {
      extractedParams[key] = value;
    }
  });

  const decodedSearch = decodeURIComponent((search || '').replace(/^\?/, ''));
  if (!decodedSearch.includes('<6><5>')) {
    return extractedParams;
  }

  let pendingKey = null;
  decodedSearch
    .split('<6><5>')
    .forEach((part) => {
      const trimmedPart = String(part || '').trim();
      if (!trimmedPart) return;

      const separatorIndex = trimmedPart.indexOf(':');
      if (separatorIndex > 0) {
        const key = trimmedPart.slice(0, separatorIndex).trim();
        const value = trimmedPart.slice(separatorIndex + 1).trim();

        if (!keysToExtract.includes(key)) {
          pendingKey = null;
          return;
        }

        if (value !== '') {
          extractedParams[key] = value;
          pendingKey = null;
        } else {
          pendingKey = key;
        }
        return;
      }

      if (pendingKey && keysToExtract.includes(pendingKey)) {
        extractedParams[pendingKey] = trimmedPart;
        pendingKey = null;
      }
    });

  return extractedParams;
};

const normalizePatientContextFields = (authToken = {}, launchContextParams = {}) => {
  const normalizedAuthToken = {
    ...authToken,
    ...launchContextParams,
  };

  if (!normalizedAuthToken.dateOfBirth && normalizedAuthToken.DOB) {
    normalizedAuthToken.dateOfBirth = normalizedAuthToken.DOB;
  }

  return normalizedAuthToken;
};

// launch is single-use on Epic's side; drop it from the address bar so a page
// refresh/back-nav can't resubmit the same (now-invalid) code.
const stripLaunchParamFromUrl = () => {
  if (typeof window === 'undefined') return;
  const cleanUrl = new URL(window.location.href);
  if (!cleanUrl.searchParams.has('launch')) return;
  cleanUrl.searchParams.delete('launch');
  window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
};

const useAuthentication = (location) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [authToken, setAuthToken] = useState(null);
  // Same shape as useTask's errorDetails so HomePage can render one shared "Technical Details" panel.
  const [authErrorDetails, setAuthErrorDetails] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const launchCode = params.get('launch');
    const journeyId = getJourneyId();
    const { requestId } = createRequestContext('auth', journeyId);
    const launchContextParams = parseLaunchContextParams(location.search);
    let stage = 'auth';
    const authenticateWithLaunchCode = async () => {
      setLoading(true);
      try {
        const launchProperties = getLaunchProperties(launchCode);
        trackTelemetry('SutterSync.Auth.Start', { journeyId, requestId, ...launchProperties, ...getRawLaunchProperty(launchCode) });
        console.log('[SutterSync][Auth][START]', {
          requestId,
          journeyId,
          hasLaunchCode: Boolean(launchCode),
          launchCodeLength: launchCode ? String(launchCode).length : 0,
        });
        if (isEpicHandshakeLogEnabled()) {
          console.log('🚨 [EPIC_HANDSHAKE_RAW][Auth][LAUNCH_CODE_RECEIVED]', {
            requestId,
            journeyId,
            launchCode,
            search: location.search,
          });
        }

        let response;
        for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
          try {
            response = await axios.get(
              `${url}/api/v1/auth`,
              {
                params: { launch: launchCode },
                headers: {
                  'Content-Type': 'application/json',
                  'x-request-id': requestId,
                  'x-correlation-id': journeyId,
                }
              }
            );
            break;
          } catch (fetchErr) {
            if (attempt <= MAX_RETRIES && isNetworkError(fetchErr)) {
              console.log('[SutterSync][Auth][RETRY]', {
                stage,
                requestId,
                journeyId,
                attempt,
                maxRetries: MAX_RETRIES,
                errorCode: fetchErr.code || null,
                errorMessage: fetchErr.message || null,
              });
              trackTelemetry('SutterSync.Auth.Retry', {
                stage,
                journeyId,
                requestId,
                attempt,
                maxRetries: MAX_RETRIES,
                errorCode: fetchErr.code || null,
                errorMessage: fetchErr.message || null,
              });
              await sleep(1000 * attempt);
              continue;
            }
            throw fetchErr;
          }
        }
        const result = response.data;
        console.log('[SutterSync][Auth][AUTH_RESPONSE]', {
          requestId,
          journeyId,
          hasRedirectUrl: Boolean(result?.redirectUrl),
        });
        if (isEpicHandshakeLogEnabled()) {
          console.log('🚨 [EPIC_HANDSHAKE_RAW][Auth][RAW_EPIC_REDIRECT_RESPONSE]', {
            requestId,
            journeyId,
            result,
          });
        }

        if (result.redirectUrl) {
          const parseRedirectUrl = new URL(result.redirectUrl);
          const code = parseRedirectUrl.searchParams.get('code');
          const state = parseRedirectUrl.searchParams.get('state');
          const epicError = parseRedirectUrl.searchParams.get('error');
          const epicErrorDescription = parseRedirectUrl.searchParams.get('error_description');

          console.log('[SutterSync][Auth][CALLBACK_PREPARE]', {
            requestId,
            hasCodeInRedirectUrl: Boolean(code),
            codeLength: code ? String(code).length : 0,
            hasStateInRedirectUrl: Boolean(state),
            epicError,
            epicErrorDescription,
          });
          if (isEpicHandshakeLogEnabled()) {
            console.log('🚨 [EPIC_HANDSHAKE_RAW][Auth][RAW_CALLBACK_PARAMS]', {
              requestId,
              redirectUrl: result.redirectUrl,
              code,
              state,
              epicError,
              epicErrorDescription,
            });
          }

          // Epic refused to redeem the launch token (e.g. already used, expired) and
          // sent error/error_description instead of code — stop here rather than
          // forwarding a codeless request that only yields an opaque 400 from our callback.
          if (epicError) {
            console.error('[SutterSync][Auth][EPIC_REDIRECT_ERROR]', {
              requestId,
              journeyId,
              epicError,
              epicErrorDescription,
            });
            trackTelemetry('SutterSync.Auth.EpicError', {
              journeyId,
              requestId,
              epicError,
              epicErrorDescription,
              ...getLaunchProperties(launchCode),
            });
            setAuthErrorDetails({
              operation: 'auth',
              stageLabel: 'Authorization Redirect',
              endpoint: `${url}/api/v1/auth`,
              requestId,
              journeyId,
              timestamp: new Date().toISOString(),
              errorCode: epicError,
              message: epicErrorDescription || epicError,
              isNetworkError: false,
              status: null,
              ...getLaunchProperties(launchCode),
            });
            setMessage(
              epicError === 'invalid_grant'
                ? 'This link has already been used. Please return to your To Dos and open it again.'
                : (epicErrorDescription || 'Authorization was declined by Epic.')
            );
            stripLaunchParamFromUrl();
            return;
          }

          stage = 'callback';
          let response;
          for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
            try {
              response = await axios.get(
                `${url}/api/v1/auth/callback`,
                {
                  params: { code, state },
                  headers: {
                    'x-request-id': requestId,
                    'x-correlation-id': journeyId,
                  }
                }
              );
              break;
            } catch (fetchErr) {
              if (attempt <= MAX_RETRIES && isNetworkError(fetchErr)) {
                console.log('[SutterSync][Auth][RETRY]', {
                  stage,
                  requestId,
                  journeyId,
                  attempt,
                  maxRetries: MAX_RETRIES,
                  errorCode: fetchErr.code || null,
                  errorMessage: fetchErr.message || null,
                });
                trackTelemetry('SutterSync.Auth.Retry', {
                  stage,
                  journeyId,
                  requestId,
                  attempt,
                  maxRetries: MAX_RETRIES,
                  errorCode: fetchErr.code || null,
                  errorMessage: fetchErr.message || null,
                });
                await sleep(1000 * attempt);
                continue;
              }
              throw fetchErr;
            }
          }
          const { authToken } = response.data;

          console.log('[SutterSync][Auth][CALLBACK_SUCCESS]', {
            requestId,
            journeyId,
            hasAuthToken: Boolean(authToken),
            authTokenKeys: authToken ? Object.keys(authToken).length : 0,
            taskRefIdPresent: Boolean(authToken?.TASKREFID),
            taskInstPresent: Boolean(authToken?.taskInst),
            epicTaskIdPresent: Boolean(authToken?.taskID),
            authContextPresent: Boolean(authToken?.AuthContext || authToken?.authContext || authToken?.Context || authToken?.context),
          });
          if (isEpicHandshakeLogEnabled()) {
            console.log('🚨 [EPIC_HANDSHAKE_RAW][Auth][RAW_AUTH_TOKEN_RESPONSE]', {
              requestId,
              journeyId,
              authToken,
            });
          }

          setAuthToken(normalizePatientContextFields(authToken, launchContextParams));
          trackTelemetry('SutterSync.Auth.Success', { journeyId, requestId });
          stripLaunchParamFromUrl();
        } else if (result.data) {
          setMessage('Authorization successful.');
          stripLaunchParamFromUrl();
        } else {
          setMessage('Unexpected response from the server.');
        }
      } catch (error) {
        console.error('[SutterSync][Auth][ERROR]', {
          requestId,
          journeyId,
          status: error?.response?.status || null,
          code: error?.code || null,
          message: error?.message || null,
          serverMessage: error?.response?.data?.message || null,
        });
        if (isEpicHandshakeLogEnabled()) {
          console.error('🚨 [EPIC_HANDSHAKE_RAW][Auth][RAW_ERROR_RESPONSE]', {
            requestId,
            journeyId,
            launchCode,
            responseData: error?.response?.data,
            responseHeaders: error?.response?.headers,
          });
        }
        trackTelemetry('SutterSync.Auth.Error', {
          journeyId,
          requestId,
          status: error?.response?.status || null,
          errorCode: error?.code || null,
          ...getLaunchProperties(launchCode),
        });

        // Same shape as useTask's errorDetails so HomePage can render one shared "Technical Details" panel.
        const stageLabel = stage === 'callback' ? 'Token Exchange (Epic Callback)' : 'Authorization Redirect';
        const debugAuthErrorDetails = {
          operation: stage,
          stageLabel,
          endpoint: error?.config?.url || `${url}/api/v1/${stage === 'callback' ? 'auth/callback' : 'auth'}`,
          requestId,
          journeyId,
          timestamp: new Date().toISOString(),

          requestMethod: error?.config?.method || 'get',
          requestUrl: error?.config?.url || null,
          requestHeaders: error?.config?.headers || null,

          errorName: error?.name || null,
          errorCode: error?.code || null,
          message: error?.message || null,
          isNetworkError: !error?.response,
          isTimeout: error?.code === 'ECONNABORTED',
          status: error?.response?.status ?? null,
          statusText: error?.response?.statusText ?? null,
          responseHeaders: error?.response?.headers || null,
          responseData: error?.response?.data ?? null,

          stack: error?.stack || null,
          toJSON: (typeof error?.toJSON === 'function') ? error.toJSON() : null,

          ...getLaunchProperties(launchCode),
          pageUrl: typeof window !== 'undefined' ? window.location.href : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        };
        setAuthErrorDetails(debugAuthErrorDetails);

        if (isErrorDetailsEnabled()) {
          trackTelemetry('SutterSync.Auth.ErrorDump', {
            journeyId,
            requestId,
            stage,
            status: debugAuthErrorDetails.status,
            errorCode: debugAuthErrorDetails.errorCode,
            errorName: debugAuthErrorDetails.errorName,
            message: debugAuthErrorDetails.message,
            isNetworkError: debugAuthErrorDetails.isNetworkError,
            isTimeout: debugAuthErrorDetails.isTimeout,
            errorDump: JSON.stringify(debugAuthErrorDetails),
          });
        }

        if (error.response) {
          console.error('Authorization failed:', error.response.data.message);
          setMessage(error.response.data.message || 'Authorization failed.');
        } else {
          console.error('Error initiating OAuth process:', error.message);
          setMessage('Failed to initiate OAuth process.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (launchCode) {
      authenticateWithLaunchCode();
    } else {
      setMessage('No launch code or access token code found.');
      trackTelemetry('SutterSync.Auth.MissingLaunch', {
        journeyId,
        requestId,
        ...getLaunchProperties(launchCode),
      });
    }
  }, [location]);

  return { loading, authToken, message, authErrorDetails };
};

export default useAuthentication;
